/**
 * Simple Geolocation Service
 * Fetches IP on page load and gets country from backend
 */

import { useState, useEffect } from 'react';
import { GET_COUNTRY_BY_IP } from '@/Graphql/Queries';
import client from '@/Graphql/apolloClient';

export interface GeolocationResult {
  ipAddress: string;
  country: string;
  success: boolean;
  message?: string;
  error?: string;
  originalIP?: string | null; // Allow null values
  isTestMode?: boolean; // NEW: Flag to indicate if test mode was used
}

/**
 * Simple function to fetch user's IP address
 * Tests all services and shows detailed results
 */
async function fetchUserIP(): Promise<string | null> {
  const services = [
    { 
      name: 'IPify', 
      fn: async () => {
        const response = await fetch('https://api.ipify.org?format=json');
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        return data.ip;
      }
    },
    { 
      name: 'IP-API', 
      fn: async () => {
        const response = await fetch('http://ip-api.com/json/');
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        if (data.status === 'fail') throw new Error(data.message);
        return data.query; // IP-API returns IP in 'query' field
      }
    },
    { 
      name: 'IPInfo', 
      fn: async () => {
        const response = await fetch('https://ipinfo.io/json');
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        return data.ip;
      }
    }
  ];

  console.log('🔍 Testing all IP detection services:');
  const results: { [key: string]: string | null } = {};
  let firstSuccessfulIP: string | null = null;

  // Test all services and collect results
  for (const service of services) {
    try {
      console.log(`📡 Trying ${service.name}...`);
      const ip = await service.fn();
      results[service.name] = ip;
      console.log(`✅ ${service.name} detected IP:`, ip);
      
      // Store first successful result
      if (ip && !firstSuccessfulIP) {
        firstSuccessfulIP = ip;
      }
    } catch (error) {
      results[service.name] = null;
      console.warn(`❌ ${service.name} failed:`, error);
    }
  }

  // Show summary of all results
  console.log('📊 IP DETECTION SUMMARY:');
  Object.entries(results).forEach(([serviceName, ip]) => {
    console.log(`   ${serviceName.padEnd(8)}: ${ip || 'Failed'}`);
  });

  if (firstSuccessfulIP) {
    console.log(`🎯 Using IP from first successful service: ${firstSuccessfulIP}`);
    return firstSuccessfulIP;
  } else {
    console.error('💥 All IP services failed');
    return null;
  }
}

/**
 * Main function to get user's country by IP
 * Uses environment variables to control test mode
 */
export async function getUserCountryByIP(): Promise<GeolocationResult | null> {
  try {
    console.log('🌍 Starting geolocation...');

    // Check environment variables for test mode
    const useTestIP = import.meta.env.VITE_TEST_IP_STATUS === 'true';
    const testIP = import.meta.env.VITE_TEST_IP;

    let ipToUse: string;
    let originalDetectedIP: string | null = null;
    let isTestMode = false;

    // ALWAYS fetch the original IP for logging
    console.log('🔍 Detecting original IP address...');
    originalDetectedIP = await fetchUserIP();
    
    if (originalDetectedIP) {
      console.log('🎯 ORIGINAL DETECTED IP:', originalDetectedIP);
    } else {
      console.log('❌ Could not detect original IP');
    }

    if (useTestIP && testIP) {
      // Use test IP from environment
      ipToUse = testIP;
      isTestMode = true;
      console.log('🧪 TEST MODE ENABLED - Using test IP:', ipToUse);
      console.log('📊 IP Comparison:');
      console.log('   Original IP:', originalDetectedIP || 'Not detected');
      console.log('   Test IP:    ', ipToUse);
    } else {
      // Use real IP
      if (!originalDetectedIP) {
        return {
          ipAddress: '',
          country: '',
          success: false,
          error: 'Could not detect IP address',
          originalIP: originalDetectedIP,
          isTestMode: false
        };
      }
      ipToUse = originalDetectedIP;
      console.log('✅ PRODUCTION MODE - Using detected IP:', ipToUse);
    }

    // Call GraphQL query with the IP
    console.log('📡 Querying backend for country with IP:', ipToUse);
    const { data } = await client.query({
      query: GET_COUNTRY_BY_IP,
      variables: { ipAddress: ipToUse },
      fetchPolicy: 'no-cache'
    });

    const result = data?.getCountryByIp;
    
    if (!result) {
      return {
        ipAddress: ipToUse,
        country: '',
        success: false,
        error: 'No response from backend',
        originalIP: originalDetectedIP,
        isTestMode
      };
    }

    console.log('📝 Backend response:', result);
    
    // Enhanced logging
    if (isTestMode) {
      console.log('🎭 GEOLOCATION SUMMARY (TEST MODE):');
      console.log('   Original IP:    ', originalDetectedIP || 'Not detected');
      console.log('   Test IP Used:   ', ipToUse);
      console.log('   Country Result: ', result.country);
      console.log('   Success:        ', result.success);
    } else {
      console.log('🌍 GEOLOCATION SUMMARY (PRODUCTION):');
      console.log('   Detected IP:    ', originalDetectedIP);
      console.log('   Country Result: ', result.country);
      console.log('   Success:        ', result.success);
    }

    return {
      ipAddress: result.ipAddress || ipToUse,
      country: result.country || '',
      success: result.success || false,
      message: result.message,
      originalIP: originalDetectedIP,
      isTestMode
    };

  } catch (error) {
    console.error('💥 Geolocation error:', error);
    return {
      ipAddress: '',
      country: '',
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
      originalIP: null,
      isTestMode: false
    };
  }
}

/**
 * Hook for React components
 */
export function useGeolocation() {
  const [result, setResult] = useState<GeolocationResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchLocation() {
      try {
        setLoading(true);
        setError(null);
        const geoResult = await getUserCountryByIP();
        setResult(geoResult);
        
        if (!geoResult?.success) {
          setError(geoResult?.error || 'Geolocation failed');
        }
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : 'Unknown error';
        setError(errorMsg);
      } finally {
        setLoading(false);
      }
    }

    fetchLocation();
  }, []);

  return { result, loading, error };
}